import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Input,
  Button,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { signOut, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { getFriends, getFollowers, getFollowing } from '../services/socialService';
import userService from '../services/userService';
import { tutorialService } from '../services/tutorialService';
import {
  ProfileAvatar,
  EditPersonalInfoModal,
  ChangePasswordModal,
  PersonalLimitsModal,
  FriendsModal,
  SettingsMenuItem,
  SupportChatModal,
  VerificationStatusModal,
} from '../components/profile';
import SkillCardModal from '../components/skill/SkillCardModal';
import { API_BASE } from '../utils/apiBase';

//  Voice Pass Modal

import VoicePassModal from '../components/profile/VoicePassModal';
import { isIOSVoiceVideoBlocked } from '../utils/iosUtils';

//  Two-Factor Authentication

import TwoFactorModal from '../components/profile/TwoFactorModal';
import { get2FAStatus, TwoFactorStatus } from '../services/twoFactorService';

//  NEW: Avatar selector import

import AvatarSelector from '../components/avatar/AvatarSelector';
import PokerIntelCard from '../components/profile/PokerIntelCard';
import NotificationSettingsModal from '../components/profile/NotificationSettingsModal';
import BlockedPlayersModal from '../components/profile/BlockedPlayersModal';
import { Ban } from 'lucide-react';

/**
 * A PSS roster avatar stores the square bust as photoURL; the matching
 * full-body render lives beside it. When the player uses one, the profile
 * page shows the character full-height - the little circle everywhere else
 * stays the bust.
 */
const pssFullBodyOf = (photoURL: string | null | undefined): string | null => {
  const m = /\/avatars\/pss\/([a-z0-9_-]+)_bust\.svg/i.exec(photoURL || '');
  return m ? `/avatars/pss/${m[1]}.svg` : null;
};

// Icons as simple components
const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const SkillCardIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M7 14l4-4 4 4 5-5" />
  </svg>
);

const HandIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v0M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v6M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8" />
    <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
  </svg>
);

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

//  2FA Key Icon

const KeyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

//  Play/Tutorial icon

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polygon points="10 8 16 12 10 16 10 8" />
  </svg>
);

//  NEW: Avatar edit icon

const PencilIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

//  Agent Dashboard Icon

const AgentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

//  Admin Dashboard Icon

const AdminIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

//  Voice Pass Icon

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
    <line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const ProfilePage: React.FC = () => {
  const { userProfile, currentUser, loading: authLoading, refreshProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [socialCounts, setSocialCounts] = useState({
    friends: 0,
    followers: 0,
    following: 0,
  });
  const [socialLoading, setSocialLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  //  NEW: Avatar editing state

  const [isEditingAvatar, setIsEditingAvatar] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState<string>('');

  //  Admin/Agent status checks

  const ADMIN_UID = '11tBjYey4AgwlZKbVg0SaqLXhtp2';
  const [isAgent, setIsAgent] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Modal states
  const editInfoModal = useDisclosure();
  const notificationsModal = useDisclosure();
  const passwordModal = useDisclosure();
  const limitsModal = useDisclosure();
  const blockedModal = useDisclosure();
  const friendsModal = useDisclosure();
  const supportModal = useDisclosure();
  const verificationModal = useDisclosure();
  const voicePassModal = useDisclosure();

  //  NEW: Tutorial menu state

  const tutorialMenuModal = useDisclosure();

  //  NEW: Delete account state

  const deleteModal = useDisclosure();
  const skillCardModal = useDisclosure();
  const twoFactorModal = useDisclosure();
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorStatus>({ enabled: false });
  const deleteConfirmRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading]);

  //  Load 2FA status

  useEffect(() => {
    const load2FAStatus = async () => {
      if (currentUser?.uid) {
        try {
          const status = await get2FAStatus();
          setTwoFactorStatus(status);
        } catch (err) {
          console.error('Error loading 2FA status:', err);
        }
      }
    };
    load2FAStatus();
  }, [currentUser?.uid]);

  //  NEW: Load current avatar when profile loads

  useEffect(() => {
    if (userProfile?.photoURL) {
      setCurrentAvatar(userProfile.photoURL);
    }
  }, [userProfile?.photoURL]);

  //  Check admin/agent status

  useEffect(() => {
    const checkRoles = async () => {
      if (!currentUser?.uid) {
        setIsAdmin(false);
        setIsAgent(false);
        return;
      }

      // Check admin by UID
      if (currentUser.uid === ADMIN_UID) {
        setIsAdmin(true);
      } else if (userProfile?.isAdmin === true || userProfile?.role === 'admin') {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }

      // Check agent status from profile first
      if (userProfile?.isAgent === true) {
        setIsAgent(true);
        return;
      }

      // Fallback: check agents collection directly
      try {
        const agentDoc = await getDoc(doc(db, 'agents', currentUser.uid));
        if (agentDoc.exists() && agentDoc.data().active === true) {
          setIsAgent(true);
        } else {
          setIsAgent(false);
        }
      } catch (error) {
        console.error('Error checking agent status:', error);
        setIsAgent(false);
      }
    };

    checkRoles();
  }, [currentUser?.uid, userProfile?.isAdmin, userProfile?.isAgent, userProfile?.role]);

  // Load social counts fresh from Firestore
  const loadSocialCounts = async () => {
    if (!currentUser?.uid || authLoading) return;
    
    setSocialLoading(true);
    try {
      const [friendsData, followersData, followingData] = await Promise.all([
        getFriends(currentUser.uid),
        getFollowers(currentUser.uid),
        getFollowing(currentUser.uid),
      ]);
      
      setSocialCounts({
        friends: friendsData.length,
        followers: followersData.length,
        following: followingData.length,
      });
    } catch (error) {
      console.error('Error loading social counts:', error);
      // Keep showing 0s on error - don't break the page
      setSocialCounts({
        friends: 0,
        followers: 0,
        following: 0,
      });
    } finally {
      setSocialLoading(false);
    }
  };

  useEffect(() => {
    // Only load social counts after auth is complete
    if (!authLoading && currentUser?.uid) {
      loadSocialCounts();
    }
  }, [currentUser?.uid, authLoading]);

  // Refresh social counts when friends modal closes
  const handleFriendsModalClose = () => {
    friendsModal.onClose();
    loadSocialCounts();
  };

  //  NEW: Avatar change handler

  const handleAvatarChange = async (newAvatarUrl: string) => {
    if (currentUser?.uid) {
      try {
        await userService.updateProfilePhoto(currentUser.uid, newAvatarUrl);
        setCurrentAvatar(newAvatarUrl);
        setIsEditingAvatar(false);
        await refreshProfile();
        toast({
          title: 'Avatar updated!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Failed to update avatar:', error);
        toast({
          title: 'Update failed',
          description: 'Could not update avatar',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast({
        title: 'Signed Out',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      navigate('/login');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to sign out',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  //  NEW: Handle account deletion

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast({
        title: 'Confirmation Required',
        description: 'Please type DELETE to confirm account deletion',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!auth.currentUser) {
      toast({
        title: 'Error',
        description: 'No user signed in',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setIsDeleting(true);
    
    try {
      // Re-authenticate user first (required for sensitive operations)
      if (deletePassword && auth.currentUser.email) {
        const credential = EmailAuthProvider.credential(
          auth.currentUser.email,
          deletePassword
        );
        await reauthenticateWithCredential(auth.currentUser, credential);
      }

      // Get auth token for API call
      const token = await auth.currentUser.getIdToken();
      
      //  Call secure server-side deletion endpoint

      const response = await fetch(`${API_BASE}/deleteAccount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ confirmText: 'DELETE' }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete account');
      }
      
      toast({
        title: 'Account Deleted',
        description: 'Your account has been permanently deleted.',
        status: 'success',
        duration: 4000,
      });
      
      // Sign out locally and redirect
      await signOut(auth);
      navigate('/login');
      
    } catch (error: any) {
      console.error('Delete account error:', error);
      
      if (error.code === 'auth/requires-recent-login' || error.code === 'auth/wrong-password') {
        toast({
          title: 'Authentication Required',
          description: 'Please enter your correct password to confirm deletion.',
          status: 'warning',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete account. Please try again.',
          status: 'error',
          duration: 4000,
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <Box 
        minH="100vh" 
        bg="black" 
        display="flex" 
        alignItems="center" 
        justifyContent="center"
      >
        <Spinner color="orange.400" size="xl" />
      </Box>
    );
  }

  return (
    <Box 
      minH="100vh" 
      bg="black" 
      pb={{ base: '100px', md: '40px' }}
      pt={{ base: 4, md: 8 }}
    >
      {/* Header */}
      <HStack 
        justify="center" 
        py={4} 
        px={4}
        borderBottom="1px solid"
        borderColor="gray.900"
      >
        <Text color="white" fontSize="lg" fontWeight="semibold">
          {userProfile?.username || 'Profile'}
        </Text>
      </HStack>

      <VStack spacing={6} px={4} py={6} maxW="500px" mx="auto">
        {/* Profile Header with Avatar */}
        <VStack spacing={4}>
          {/*  NEW: Avatar with edit capability */}

          {isEditingAvatar ? (
            <Box 
              bg="#1C1C1E" 
              p={4} 
              borderRadius="xl" 
              w="100%"
              border="1px solid"
              borderColor="orange.500"
            >
              <Text color="white" fontWeight="semibold" mb={3} textAlign="center">
                Choose Your Avatar
              </Text>
              <AvatarSelector
                currentAvatar={currentAvatar}
                onAvatarSelect={handleAvatarChange}
                userId={currentUser?.uid || ''}
              />
              <Box
                as="button"
                mt={3}
                w="100%"
                py={2}
                bg="gray.700"
                color="white"
                borderRadius="lg"
                fontSize="sm"
                _hover={{ bg: 'gray.600' }}
                onClick={() => setIsEditingAvatar(false)}
              >
                Cancel
              </Box>
            </Box>
          ) : (
            <Box position="relative">
              {(() => {
                const fullBody = pssFullBodyOf(currentAvatar || userProfile?.photoURL);
                if (fullBody) {
                  // Full-height character stage - the whole point of the
                  // roster art. The bust circle still represents the player
                  // everywhere small.
                  return (
                    <Box
                      w="220px" h="300px" display="grid" placeItems="center"
                      bg="radial-gradient(ellipse at 50% 88%, rgba(0,217,217,0.12), transparent 62%)"
                    >
                      <img
                        src={fullBody}
                        alt="Your avatar"
                        style={{
                          width: 200, height: 290,
                          filter: 'drop-shadow(0 18px 22px rgba(0,0,0,0.55))',
                        }}
                      />
                    </Box>
                  );
                }
                return (
                  <ProfileAvatar
                    userId={currentUser?.uid || ''}
                    photoURL={currentAvatar || userProfile?.photoURL}
                    fullName={userProfile?.fullName}
                    username={userProfile?.username}
                    size="xl"
                    editable={false}
                  />
                );
              })()}
              {/* Edit Avatar Button */}
              <Box
                as="button"
                position="absolute"
                bottom={0}
                right={0}
                bg="orange.500"
                color="white"
                w="32px"
                h="32px"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="3px solid black"
                _hover={{ bg: 'orange.400', transform: 'scale(1.1)' }}
                transition="all 0.2s"
                onClick={() => setIsEditingAvatar(true)}
              >
                <PencilIcon />
              </Box>
            </Box>
          )}
          
          <VStack spacing={1}>
            <Text color="white" fontSize="xl" fontWeight="bold">
              {userProfile?.showFullName && userProfile?.fullName 
                ? userProfile.fullName 
                : userProfile?.username || 'Player'}
            </Text>
            <Text color="gray.500" fontSize="sm">
              @{userProfile?.username}
            </Text>
          </VStack>

          {/* Stats Row */}
          <HStack spacing={8} pt={2}>
            <VStack 
              spacing={0} 
              cursor="pointer"
              onClick={friendsModal.onOpen}
              _hover={{ opacity: 0.8 }}
            >
              {socialLoading ? (
                <Spinner size="sm" color="white" />
              ) : (
                <Text color="white" fontSize="lg" fontWeight="bold">
                  {socialCounts.friends}
                </Text>
              )}
              <Text color="gray.500" fontSize="xs">Friends</Text>
            </VStack>
            <VStack 
              spacing={0}
              cursor="pointer"
              onClick={friendsModal.onOpen}
              _hover={{ opacity: 0.8 }}
            >
              {socialLoading ? (
                <Spinner size="sm" color="white" />
              ) : (
                <Text color="white" fontSize="lg" fontWeight="bold">
                  {socialCounts.followers}
                </Text>
              )}
              <Text color="gray.500" fontSize="xs">Followers</Text>
            </VStack>
            <VStack 
              spacing={0}
              cursor="pointer"
              onClick={friendsModal.onOpen}
              _hover={{ opacity: 0.8 }}
            >
              {socialLoading ? (
                <Spinner size="sm" color="white" />
              ) : (
                <Text color="white" fontSize="lg" fontWeight="bold">
                  {socialCounts.following}
                </Text>
              )}
              <Text color="gray.500" fontSize="xs">Following</Text>
            </VStack>
          </HStack>

          {/* Bio */}
          {userProfile?.bio && (
            <Text 
              color="gray.400" 
              fontSize="sm" 
              textAlign="center" 
              maxW="300px"
              pt={2}
            >
              {userProfile.bio}
            </Text>
          )}
        </VStack>

        {/* Poker Intel - the player's own card (VPIP/PFR/showdowns/net bb),
            split by cash and tournament, same data as tapping a seat. */}
        {currentUser?.uid && <PokerIntelCard userId={currentUser.uid} />}

        {/* General Settings Section */}
        <Box w="100%">
          <Text color="gray.500" fontSize="xs" fontWeight="semibold" mb={3} px={1}>
            General settings
          </Text>
          <VStack spacing={2} align="stretch">
            <SettingsMenuItem
              icon={<UserIcon />}
              label="Edit personal info"
              onClick={editInfoModal.onOpen}
            />
            <SettingsMenuItem
              icon={<HandIcon />}
              label="Personal limits"
              onClick={limitsModal.onOpen}
            />
            <SettingsMenuItem
              icon={<Ban size={20} />}
              label="Blocked players"
              onClick={blockedModal.onOpen}
            />
            <SettingsMenuItem
              icon={<ChatIcon />}
              label="Notifications"
              onClick={notificationsModal.onOpen}
            />
            <SettingsMenuItem
              icon={<LockIcon />}
              label="Change password"
              onClick={passwordModal.onOpen}
            />
            <SettingsMenuItem
              icon={<KeyIcon />}
              label="Two-factor authentication"
              onClick={twoFactorModal.onOpen}
              rightElement={
                <HStack spacing={2}>
                  <Text 
                    color={twoFactorStatus.enabled ? 'green.400' : 'gray.500'} 
                    fontSize="xs"
                  >
                    {twoFactorStatus.enabled ? 'Enabled' : 'Disabled'}
                  </Text>
                  <Text color="gray.600" fontSize="lg">›</Text>
                </HStack>
              }
            />
            <SettingsMenuItem
              icon={<ShieldIcon />}
              label="Prize eligibility"
              onClick={() => navigate('/eligibility')}
              rightElement={
                <HStack spacing={2}>
                  <Text 
                    color={userProfile?.kycStatus === 'verified' ? 'green.400' : 'orange.400'} 
                    fontSize="xs"
                  >
                    {userProfile?.kycStatus === 'verified' ? 'Confirmed' : 'Not confirmed'}
                  </Text>
                  <Text color="gray.600" fontSize="lg">›</Text>
                </HStack>
              }
            />
            {!isIOSVoiceVideoBlocked() && (
              <SettingsMenuItem
                icon={<MicIcon />}
                label="Voice Chat Pass"
                onClick={voicePassModal.onOpen}
                rightElement={
                  <HStack spacing={2}>
                    <Text color="cyan.400" fontSize="xs">
                      24hr unlimited
                    </Text>
                    <Text color="gray.600" fontSize="lg">›</Text>
                  </HStack>
                }
              />
            )}
          </VStack>
        </Box>

        {/*  Agent/Admin Dashboards Section - Only shows if user is agent or admin */}

        {(isAgent || isAdmin) && (
          <Box w="100%">
            <Text color="gray.500" fontSize="xs" fontWeight="semibold" mb={3} px={1}>
              Dashboards
            </Text>
            <VStack spacing={2} align="stretch">
              <SettingsMenuItem
                icon={<Ban size={20} style={{ opacity: 0 }} />}
                label="My recordings"
                onClick={() => navigate('/recordings')}
                rightElement={
                  <HStack spacing={2}>
                    <Text color="gray.400" fontSize="xs">Table videos</Text>
                    <Text color="gray.600" fontSize="lg">›</Text>
                  </HStack>
                }
              />
              {/* Agent Dashboard - only show if user is agent */}
              {isAgent && (
                <SettingsMenuItem
                  icon={<AgentIcon />}
                  label="Agent Dashboard"
                  onClick={() => navigate('/agent')}
                  rightElement={
                    <HStack spacing={2}>
                      <Text color="#FFD700" fontSize="xs">
                        Earn rake
                      </Text>
                      <Text color="gray.600" fontSize="lg">›</Text>
                    </HStack>
                  }
                />
              )}
              
              {/* Admin Dashboard - only show if user is admin */}
              {isAdmin && (
                <SettingsMenuItem
                  icon={<AdminIcon />}
                  label="Admin Dashboard"
                  onClick={() => navigate('/admin')}
                  rightElement={
                    <HStack spacing={2}>
                      <Text color="#00D9D9" fontSize="xs">
                        Manage
                      </Text>
                      <Text color="gray.600" fontSize="lg">›</Text>
                    </HStack>
                  }
                />
              )}
            </VStack>
          </Box>
        )}

        {/* Stats hub - performance, player rating and home game ledgers in one place */}
        <Box w="100%">
          <Text color="gray.500" fontSize="xs" fontWeight="semibold" mb={3} px={1}>
            Stats
          </Text>
          <VStack spacing={2} align="stretch">
            <SettingsMenuItem
              icon={<SkillCardIcon />}
              label="Player stats"
              onClick={() => navigate('/my-stats')}
            />
          </VStack>
        </Box>

        {/* Home games - results ledgers. Game records, NOT player stats. */}
        <Box w="100%">
          <Text color="gray.500" fontSize="xs" fontWeight="semibold" mb={3} px={1}>
            Home Games
          </Text>
          <VStack spacing={2} align="stretch">
            <SettingsMenuItem
              icon={<HandIcon />}
              label="Home game ledgers"
              onClick={() => navigate('/home-ledgers')}
            />
            <SettingsMenuItem
              icon={<HandIcon />}
              label="Groups"
              onClick={() => navigate('/groups')}
            />
          </VStack>
        </Box>

        {/* Support Hub Section */}
        <Box w="100%" data-tutorial="support-hub">
          <Text color="gray.500" fontSize="xs" fontWeight="semibold" mb={3} px={1}>
            Support Hub
          </Text>
          <VStack spacing={2} align="stretch">
            <Box data-tutorial="support-button">
              <SettingsMenuItem
                icon={<ChatIcon />}
                label="Support chat"
                onClick={supportModal.onOpen}
              />
            </Box>
            <SettingsMenuItem
              icon={<PlayIcon />}
              label="App Tutorials"
              onClick={tutorialMenuModal.onOpen}
              rightElement={
                <HStack spacing={2}>
                  <Text color="cyan.400" fontSize="xs">
                    Learn the app
                  </Text>
                  <Text color="gray.600" fontSize="lg">›</Text>
                </HStack>
              }
            />
            <SettingsMenuItem
              icon={<LogoutIcon />}
              label="Log out"
              onClick={handleLogout}
              variant="danger"
            />
          </VStack>
        </Box>

        {/* Referral Code */}
        <Box 
          w="100%" 
          bg="#1C1C1E" 
          p={4} 
          borderRadius="xl"
          borderLeft="4px solid"
          borderColor="orange.400"
        >
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <VStack align="start" spacing={0}>
                <Text color="gray.400" fontSize="xs">YOUR REFERRAL LINK</Text>
                <Text color="orange.400" fontSize="sm" fontWeight="bold" fontFamily="mono" wordBreak="break-all">
                  {userProfile?.referralCode ? `pokersweepstars.web.app/login?ref=${userProfile.referralCode}` : 'Loading...'}
                </Text>
                <Text color="gray.600" fontSize="xs">
                  Friends get $5 PSS Coins bonus when they sign up!
                </Text>
              </VStack>
              <Box
                as="button"
                bg="orange.500"
                color="white"
                px={4}
                py={2}
                borderRadius="lg"
                fontSize="sm"
                fontWeight="semibold"
                flexShrink={0}
                _hover={{ bg: 'orange.600' }}
                onClick={() => {
                  const referralUrl = `https://pokersweepstars.web.app/login?ref=${userProfile?.referralCode || ''}`;
                  navigator.clipboard.writeText(referralUrl);
                  toast({
                    title: 'Copied!',
                    description: 'Referral link copied to clipboard',
                    status: 'success',
                    duration: 2000,
                  });
                }}
              >
                Copy Link
              </Box>
            </HStack>
            
            {/* Show referrer if user has one */}
            {userProfile?.referrerId && (
              <Box 
                bg="rgba(34, 197, 94, 0.1)" 
                p={2} 
                borderRadius="md"
                border="1px solid"
                borderColor="green.700"
              >
                <Text color="green.400" fontSize="xs">
                   You were referred by a friend

                </Text>
              </Box>
            )}
            
            {/* View all referrals link */}
            <Text
              color="cyan.400"
              fontSize="sm"
              fontWeight="semibold"
              cursor="pointer"
              textAlign="center"
              _hover={{ color: 'cyan.300', textDecoration: 'underline' }}
              onClick={() => navigate('/referrals')}
            >
              View Your Referrals →
            </Text>
          </VStack>
        </Box>



        {/* Wallet Summary */}
        <Box
          w="100%"
          bg="#1C1C1E"
          p={4}
          borderRadius="xl"
        >
          <HStack justify="space-between" mb={3}>
            <Text color="gray.400" fontSize="sm">Wallet Balance</Text>
            <Text
              color="orange.400"
              fontSize="sm"
              fontWeight="semibold"
              cursor="pointer"
              onClick={() => navigate('/wallet')}
              _hover={{ color: 'orange.300' }}
            >
              Manage →
            </Text>
          </HStack>
          <HStack justify="space-between">
            <VStack align="start" spacing={0}>
              <Text color="gray.500" fontSize="xs">PlayCoins</Text>
              <Text color="white" fontSize="lg" fontWeight="bold">
                {userProfile?.playCoins?.toLocaleString() || '0'}
              </Text>
            </VStack>
            <VStack align="end" spacing={0}>
              <Text color="gray.500" fontSize="xs">PSS Coins</Text>
              <Text color="green.400" fontSize="lg" fontWeight="bold">
                ${userProfile?.pkCash?.toFixed(2) || '0.00'}
              </Text>
            </VStack>
          </HStack>
        </Box>

        {/* Delete Account Link */}
        <Text 
          color="gray.600" 
          fontSize="sm" 
          cursor="pointer"
          _hover={{ color: 'red.400' }}
          pt={4}
          onClick={deleteModal.onOpen}
        >
          Delete account
        </Text>
      </VStack>

      {/* Modals */}
      <EditPersonalInfoModal
        isOpen={editInfoModal.isOpen}
        onClose={editInfoModal.onClose}
      />
      <SkillCardModal
        isOpen={skillCardModal.isOpen}
        onClose={skillCardModal.onClose}
      />
      <ChangePasswordModal
        isOpen={passwordModal.isOpen}
        onClose={passwordModal.onClose}
      />
      <NotificationSettingsModal
        isOpen={notificationsModal.isOpen}
        onClose={notificationsModal.onClose}
      />
      <PersonalLimitsModal
        isOpen={limitsModal.isOpen}
        onClose={limitsModal.onClose}
      />
      <BlockedPlayersModal
        isOpen={blockedModal.isOpen}
        onClose={blockedModal.onClose}
      />
      <FriendsModal
        isOpen={friendsModal.isOpen}
        onClose={handleFriendsModalClose}
      />
      <SupportChatModal
        isOpen={supportModal.isOpen}
        onClose={supportModal.onClose}
      />
      <VerificationStatusModal
        isOpen={verificationModal.isOpen}
        onClose={verificationModal.onClose}
      />

      {/*  Two-Factor Authentication Modal */}

      <TwoFactorModal
        isOpen={twoFactorModal.isOpen}
        onClose={twoFactorModal.onClose}
        onStatusChange={async () => {
          const status = await get2FAStatus();
          setTwoFactorStatus(status);
        }}
      />

      {/*  Voice Chat Pass Modal */}

      <VoicePassModal
        isOpen={voicePassModal.isOpen}
        onClose={voicePassModal.onClose}
      />

      {/*  Tutorial Selection Menu - Main walkthrough + Agent (if applicable) */}

      <Modal isOpen={tutorialMenuModal.isOpen} onClose={tutorialMenuModal.onClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="#0D1B2A" border="2px solid" borderColor="cyan.600" borderRadius="xl" mx={4}>
          <ModalHeader color="cyan.400" borderBottom="1px solid" borderColor="gray.700">
             App Tutorials

          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody py={4}>
            <VStack spacing={3} align="stretch">
              <Text color="gray.400" fontSize="sm" mb={2}>
                Replay the app walkthrough to learn all features:
              </Text>
              
              {/* Main App Tour - Full walkthrough */}
              <Box
                as="button"
                p={4}
                bg="rgba(0,217,217,0.1)"
                border="1px solid"
                borderColor="cyan.700"
                borderRadius="lg"
                textAlign="left"
                _hover={{ bg: 'rgba(0,217,217,0.2)', borderColor: 'cyan.500' }}
                onClick={async () => {
                  if (currentUser?.uid) {
                    await tutorialService.resetTutorial(currentUser.uid, 'main');
                    tutorialMenuModal.onClose();
                    navigate('/?startTutorial=true');
                  }
                }}
              >
                <HStack justify="space-between">
                  <VStack align="start" spacing={1}>
                    <Text color="white" fontWeight="bold" fontSize="lg"> Full App Tour</Text>

                    <Text color="gray.400" fontSize="xs">
                      Complete walkthrough: Home → Wallet → Redeem → Tables → Create Games → Referrals → Rules
                    </Text>
                  </VStack>
                  <Text color="cyan.400" fontSize="xl">→</Text>
                </HStack>
              </Box>

              {/* Agent Tutorial - Only show if user is agent */}
              {isAgent && (
                <Box
                  as="button"
                  p={4}
                  bg="rgba(255,215,0,0.1)"
                  border="1px solid"
                  borderColor="yellow.600"
                  borderRadius="lg"
                  textAlign="left"
                  _hover={{ bg: 'rgba(255,215,0,0.2)', borderColor: 'yellow.400' }}
                  onClick={async () => {
                    if (currentUser?.uid) {
                      await tutorialService.resetTutorial(currentUser.uid, 'agent');
                      tutorialMenuModal.onClose();
                      navigate('/agent?startTutorial=true&tutorialType=agent');
                    }
                  }}
                >
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text color="white" fontWeight="bold" fontSize="lg"> Agent Dashboard Tour</Text>

                      <Text color="gray.400" fontSize="xs">
                        Learn about referrals, earnings, promo codes & payouts
                      </Text>
                    </VStack>
                    <Text color="yellow.400" fontSize="xl">→</Text>
                  </HStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/*  DELETE ACCOUNT CONFIRMATION DIALOG */}

      <AlertDialog
        isOpen={deleteModal.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          deleteModal.onClose();
          setDeleteConfirmText('');
          setDeletePassword('');
        }}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.800">
          <AlertDialogContent bg="#0D1B2A" border="2px solid" borderColor="red.600" borderRadius="xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="red.400" borderBottom="1px solid" borderColor="gray.700">
               Delete Account Permanently

            </AlertDialogHeader>

            <AlertDialogBody py={4}>
              <VStack spacing={4} align="stretch">
                <Text color="gray.300">
                  This action <Text as="span" color="red.400" fontWeight="bold">cannot be undone</Text>. 
                  All your data will be permanently deleted including:
                </Text>
                <Box bg="rgba(255,0,0,0.1)" p={3} borderRadius="md" border="1px solid" borderColor="red.700">
                  <VStack align="start" spacing={1}>
                    <Text color="red.300" fontSize="sm">• Your profile and settings</Text>
                    <Text color="red.300" fontSize="sm">• Wallet balance (PSS Coins & PlayCoins)</Text>
                    <Text color="red.300" fontSize="sm">• Transaction history</Text>
                    <Text color="red.300" fontSize="sm">• Friends and followers</Text>
                    <Text color="red.300" fontSize="sm">• All game statistics</Text>
                  </VStack>
                </Box>
                
                <Box>
                  <Text color="gray.400" fontSize="sm" mb={2}>
                    Enter your password to confirm:
                  </Text>
                  <Input
                    type="password"
                    placeholder="Your password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    bg="#0a1929"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'red.400' }}
                  />
                </Box>

                <Box>
                  <Text color="gray.400" fontSize="sm" mb={2}>
                    Type <Text as="span" color="red.400" fontWeight="bold">DELETE</Text> to confirm:
                  </Text>
                  <Input
                    ref={deleteConfirmRef}
                    placeholder="Type DELETE"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                    bg="#0a1929"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'red.400' }}
                  />
                </Box>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter gap={3} borderTop="1px solid" borderColor="gray.700" pt={4}>
              <Button
                ref={cancelRef}
                onClick={() => {
                  deleteModal.onClose();
                  setDeleteConfirmText('');
                  setDeletePassword('');
                }}
                variant="outline"
                colorScheme="gray"
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteAccount}
                isLoading={isDeleting}
                loadingText="Deleting..."
                isDisabled={deleteConfirmText !== 'DELETE' || !deletePassword}
              >
                Delete My Account
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default ProfilePage;